import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, Phone, MapPin, Instagram, Linkedin, Send } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast.success("Thank you for your message! We'll get back to you within 24 hours.");
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const quickLinks = [
    { title: 'Home', path: '/' },
    { title: 'About Us', path: '/about' },
    { title: 'Products & Services', path: '/products-services' },
    { title: 'Careers', path: '/careers' },
    { title: 'News', path: '/news' },
    { title: 'Investor', path: '/investor' }
  ];

  const contactMethods = [
    {
      icon: <Mail className="h-8 w-8" />,
      title: "Email Us",
      info: "info@vincotek.com",
      action: "mailto:info@vincotek.com",
      description: "Send us an email and we'll respond within 24 hours."
    },
    {
      icon: <Phone className="h-8 w-8" />,
      title: "Call Us",
      info: "+61 (08) 123 456 789",
      action: "tel:+61081234567",
      description: "Speak directly with our team during business hours."
    },
    {
      icon: <MapPin className="h-8 w-8" />,
      title: "Visit Us",
      info: "Perth, Australia",
      action: "#",
      description: "Schedule a meeting at our office location."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-blue-50/30 to-yellow-50/20 py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight mb-6">
              Get in{' '}
              <span className="bg-gradient-to-r from-vincotek-yellow to-vincotek-blue bg-clip-text text-transparent">
                Touch
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
              Ready to start your next project or have questions about our services? 
              We'd love to hear from you. Reach out and let's explore how we can help transform your ideas into reality.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              How to Reach Us
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the method that works best for you. We're here to help and answer any questions you might have.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactMethods.map((method, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center bg-white border-0">
                <CardContent className="p-8">
                  <div className="text-vincotek-blue mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    {method.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-3">
                    {method.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {method.description}
                  </p>
                  <a 
                    href={method.action}
                    className="inline-block text-lg font-medium text-vincotek-blue hover:text-vincotek-dark transition-colors"
                  >
                    {method.info}
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Social Media */}
          <div className="text-center">
            <h3 className="text-2xl font-semibold text-vincotek-dark mb-6">
              Follow Us
            </h3>
            <div className="flex justify-center space-x-6">
              <a href="#" className="text-gray-400 hover:text-vincotek-blue transition-colors p-3 rounded-full hover:bg-blue-50">
                <Instagram className="h-8 w-8" />
              </a>
              <a href="#" className="text-gray-400 hover:text-vincotek-blue transition-colors p-3 rounded-full hover:bg-blue-50">
                <Linkedin className="h-8 w-8" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Form */}
            <div>
              <Card className="bg-white border-0 shadow-2xl">
                <CardContent className="p-8">
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold text-vincotek-dark mb-4">
                      Send Us a Message
                    </h2>
                    <p className="text-gray-600">
                      Fill out the form below and we'll get back to you as soon as possible.
                    </p>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="h-12"
                          placeholder="Your full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="h-12"
                          placeholder="your.email@example.com"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject</Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className="h-12"
                        placeholder="What's this about?"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        placeholder="Tell us about your project or inquiry..."
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-vincotek-blue hover:bg-blue-600 text-white h-12 rounded-full font-semibold transition-all duration-300 hover:shadow-lg group"
                    >
                      Send Message
                      <Send className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
            
            {/* Quick Links & Info */}
            <div className="space-y-8">
              <Card className="bg-white border-0 shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold text-vincotek-dark mb-6">
                    Quick Links
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {quickLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.path}
                        className="text-gray-600 hover:text-vincotek-blue transition-colors py-2 border-b border-gray-100 hover:border-vincotek-blue"
                      >
                        {link.title}
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-vincotek-dark to-gray-800 text-white border-0">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold mb-6">
                    Why Choose Vincotek?
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-vincotek-yellow rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-300">Expert team with proven track record</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-vincotek-yellow rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-300">Innovative solutions tailored to your needs</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-vincotek-yellow rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-300">Commitment to social impact and giving back</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-vincotek-yellow rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-300">Transparent communication and collaboration</span>
                    </li>
                  </ul>
                  
                  <div className="mt-8 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                    <p className="text-vincotek-yellow font-semibold mb-2">Response Time</p>
                    <p className="text-gray-300 text-sm">We typically respond to inquiries within 24 hours during business days.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map/Location Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Visit Our Office
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Located in the heart of Sydney, Australia. Schedule a meeting to discuss your project in person.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-vincotek-yellow/20 to-vincotek-blue/20 rounded-2xl p-8 text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-2xl font-semibold text-vincotek-dark mb-4">
              Vincotek Headquarters
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              Perth, Australia<br />
              Business Hours: Monday - Friday, 9:00 AM - 5:00 PM AWST
            </p>
            <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
              Schedule a Visit
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;